import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import { Button, Popover, Typography } from "@mui/material";
import { useAppSelector } from "src/redux/storeHooks";
import { PlatformDataType } from 'src/interfaces/Platform.interface'
import clsx from 'clsx'
import { KTIcon } from "src/marketConnect/helpers";
import BundleEditorModal, { BundleData } from "./CreateEdit-ActiveBundle";
import { useSelector } from "react-redux";
import { AppDispatch } from "src/redux/store";
import { useDispatch } from "react-redux";
import { formatDateLocal, formatTimeZone } from "src/utils/TimeZoneDateFormat";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import TableLoadingShell from "src/utils/TableLoadingShell";
import CommonAlertModal from "src/utils/modals/CommonAlertModal";
import { deleteBundleItemAsync, getActiveBundleListAsync } from "src/redux/actions/activeBundleAction";
import { usePermission } from "src/hooks/usePermission";
import {
    getConnectedPlatformLabel,
    getConnectedPlatformTooltip,
} from "../template-and-pricing/utils/connectedPlatformLabel";
interface BundleInfoItem {
    name: string;
    quantity: number;
    sku: string;
}

interface BundleItem {
    id?: number;
    name: string;
    parent_SKU: string;
    last_updated_at: string;
    component_count: number;
    total_qty: number;
    bundle_info: BundleInfoItem[];
    parentProductId?: string;
    sku?: string;
    components?: any[];
}

// CSS injection
const injectStyles = () => {
    const id = "active-bundles-styles";
    if (document.getElementById(id)) return;
    const css = `
  .abd-container {min-height: 420px; }
  .abd-card { background: #fff; border-radius: 10px; box-shadow: 0 6px 14px rgba(31,45,61,0.06);padding:1rem }
  .abd-header { display:flex; align-items:center; justify-content:space-between; }
  .abd-title { font-size: 28px; font-weight:700; color:#0f1724; }
  .abd-subtitle { color:#6b7280; font-size:14px; margin-top:6px }
  .abd-name { font-weight:400; font-size:12.5px; color:#111827 }
  .abd-sub { font-size:11px; color:#6b7280 }
  .abd-actions { display:flex; gap:6px; }
  .view-details-link { color:#2563eb; cursor:pointer; text-decoration:underline; font-weight:500 }

  /* Status toggle */
  .bundle-status {
    cursor: pointer;
    padding: 4px 12px;
    border-radius: 20px;
    text-align: center;
    width: 80px;
    font-weight: 600;
    color: #fff;
    user-select: none;
    transition: background-color 0.2s;
  }
  .bundle-status.active { background-color: #16a34a; }
  .bundle-status.inactive { background-color: #dc2626; }
  .abd-channel-tabs {
    display: flex;
    align-items: flex-end;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    background: #f3f4f6;
    border-bottom: 1px solid #e5e7eb;
    padding: 0.25rem 0.4rem 0;
    gap: 0.25rem;
    margin-bottom: 0.3rem;
}

.abd-channel-tabs-scroll {
    display: flex;
    align-items: flex-end;
    flex: 1;
    min-width: 0;
    gap: 0.5rem;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    list-style: none;
    flex-shrink: 1;
    margin: 0;
    padding: 0;
}

.abd-channel-tabs-scroll::-webkit-scrollbar {
    display: none;
}

.abd-channel-tab {
    appearance: none;
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    flex: 0 0 auto;
    min-height: 2.5rem;
    padding: 0.5rem 0.9rem;
    margin: 0;
    border: 1px solid transparent;
    border-bottom: 0;
    border-radius: 0.375rem 0.375rem 0 0;
    background: #e8eaee;
    color: #6b7280;
    cursor: pointer;
    white-space: nowrap;
    min-width: 0;
}

.abd-channel-tab.active {
    background: #ffffff;
    border-color: #2563eb;
    border-bottom: 1px solid #ffffff;
    color: #2563eb;
    margin-bottom: -1px;
}

.abd-channel-tab img {
    width: 1.375rem;
    height: 1.375rem;
    flex-shrink: 0;
    border-radius: 0.25rem;
    object-fit: contain;
    background: #ffffff;
    padding: 0.1rem;
}

.abd-channel-tab span {
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.2;
}

.abd-channel-tabs-nav {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-self: stretch;
    gap: 0.05rem;
    flex-shrink: 0;
    padding: 0.15rem 0.3rem 0.35rem;
    margin-left: 0.15rem;
}

.abd-channel-tabs-nav button {
    appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.15rem;
    height: 0.95rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
}

.abd-channel-tabs-nav button:disabled {
    opacity: 0.35;
    cursor: default;
}

.abd-channel-tabs-nav i {
    font-size: 0.75rem;
}
  `;
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = css;
    document.head.appendChild(style);
};

/** Same destination-marketplace rule as Listing Profiles (non-source, non-billing channels). */
const hasSourceStoreFlag = (platform: any) =>
    Object.entries(platform ?? {}).some(
        ([key, value]) =>
            key !== 'isConnected' &&
            key !== 'isActive' &&
            key !== 'isRequired' &&
            /^is[A-Z].*Store$/.test(key) &&
            value === true
    )

const isBundleDestinationPlatform = (platform: any) =>
    Boolean(platform) && !hasSourceStoreFlag(platform) && platform?.isBillingPlatform !== true

// Component
export default function ActiveBundleDefinitions() {
    const dispatch = useDispatch<AppDispatch>();
    const {isRead, isWrite, isDelete} = usePermission();
    const [searchParams] = useSearchParams();
    const platformIdFromUrl = searchParams.get('platform_id');

    const [deletedBundles, setDeletedBundles] = useState<BundleItem[]>([]);
    const { fetchedConnectedPlatformData } = useAppSelector((state) => state.fetchConnectedPlatform)
    const [secondaryStorePlatforms, setSecondaryStorePlatforms] = useState<PlatformDataType[]>([])
    const channelTabsRef = useRef<HTMLUListElement | null>(null);
    const scrollChannels = (direction: -1 | 1) => {
        if (!channelTabsRef.current) return;

        const container = channelTabsRef.current;
        const scrollAmount = Math.max(container.clientWidth * 0.6, 180);

        container.scrollBy({
            left: direction * scrollAmount,
            behavior: "smooth",
        });
    };

    const [hasChannelOverflow, setHasChannelOverflow] = useState(false);
    const [canScrollChannelsLeft, setCanScrollChannelsLeft] = useState(false);
    const [canScrollChannelsRight, setCanScrollChannelsRight] = useState(false);

    const updateChannelScrollState = useCallback(() => {
        const container = channelTabsRef.current;

        if (!container) return;

        const hasOverflow = container.scrollWidth > container.clientWidth + 1;

        setHasChannelOverflow(hasOverflow);
        setCanScrollChannelsLeft(container.scrollLeft > 1);
        setCanScrollChannelsRight(
            container.scrollLeft + container.clientWidth < container.scrollWidth - 1
        );
    }, []);

    useEffect(() => {
        const container = channelTabsRef.current;

        if (!container) return;

        updateChannelScrollState();

        const handleScroll = () => updateChannelScrollState();

        const resizeObserver = new ResizeObserver(() => {
            updateChannelScrollState();
        });

        container.addEventListener("scroll", handleScroll);
        resizeObserver.observe(container);

        return () => {
            container.removeEventListener("scroll", handleScroll);
            resizeObserver.disconnect();
        };
    }, [secondaryStorePlatforms, updateChannelScrollState]);
    const [currentActiveTab, setCurrentActiveTab] = useState(1)
    const [searchString, setSearchString] = useState("");

    // Popover state
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [popoverItems, setPopoverItems] = useState<BundleInfoItem[] | null>(null);
    // Create/Edit Popup state
    const [openPopup, setOpenPopup] = useState(false);
    const [editingBundleId, setEditingBundleId] = useState(null);
    const [currentPlatformId, setCurrentPlatformId] = useState<number | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
    const [deletingRowId, setDeletingRowId] = useState<number | null>(null);
    const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);

    // pagination + search state
    const [paginationData, setPaginationData] = useState({
        page: 1,
        limit: 10,
        sort_by: "",
        sort_order: "desc",
        search: "",
    });

    // redux state
    const { bundleList, pagination, loading } = useSelector(
        (state: any) => state.activeBundleSlice
    );
    console.log("pagination", pagination)
    // Fetch API
    const fetchBundleList = useCallback(async () => {
        if (currentPlatformId === null) return;

        try {
            const response = await dispatch(
                getActiveBundleListAsync({
                    paramData: {
                        platform_id: currentPlatformId,
                        page: paginationData.page,
                        limit: paginationData.limit,
                        sort_by: paginationData.sort_by,
                        sort_order: paginationData.sort_order,
                        search: paginationData.search,
                    },
                })
            );
            // Handle rejected thunk
            if (response?.payload?.status_code !== 200) {
                toast.error(response?.payload?.data?.massage || "Something went wrong");
            }
        } catch (error) {
            console.log("Error fetching Active Bundle List:", error);
            toast.error("Unexpected error occurred");
        }
    }, [dispatch, paginationData, currentPlatformId]);

    useEffect(() => {
        injectStyles();
    }, []);

    useEffect(() => {
        fetchBundleList();
    }, [fetchBundleList]);

    useEffect(() => {
        if (fetchedConnectedPlatformData && fetchedConnectedPlatformData.length > 0) {
            const secondaryStore: any = fetchedConnectedPlatformData.filter(isBundleDestinationPlatform)
            setSecondaryStorePlatforms(secondaryStore);

            if (platformIdFromUrl) {
                const matchedIndex = secondaryStore.findIndex(
                    (platform: any) => Number(platform?.id) === Number(platformIdFromUrl)
                )
                if (matchedIndex >= 0) {
                    setCurrentActiveTab(matchedIndex + 1)
                    setCurrentPlatformId(secondaryStore[matchedIndex]?.id ?? null)
                    return
                }
            }

            setCurrentActiveTab(1)
            setCurrentPlatformId(secondaryStore?.[0]?.id ?? null)
        } else {
            setCurrentPlatformId(null)
            setSecondaryStorePlatforms([])
        }
    }, [fetchedConnectedPlatformData, platformIdFromUrl])

    const handleCreate = () => {
        if (currentPlatformId == null) {
            toast.error("Select a destination marketplace before creating a bundle.");
            return;
        }
        setEditingBundleId(null);
        setOpenPopup(true);
    };

    const handleEdit = (row) => {
        setEditingBundleId(row?.id);
        setOpenPopup(true);
    };

    const handleDeleteClick = (index: number) => {
        setDeleteIndex(index);
        setShowDeleteModal(true);
    };

    const cancelDelete = () => {
        setDeleteIndex(null);
        setShowDeleteModal(false);
    };

    const openPopover = (event: React.MouseEvent<HTMLElement>, items: BundleInfoItem[]) => {
        setAnchorEl(event.currentTarget);
        setPopoverItems(items);
    };

    const closePopover = () => {
        setAnchorEl(null);
        setPopoverItems(null);
    };
    const confirmDelete = () => {
        if (deleteIndex === null) return;

        const rowToDelete = bundleList[deleteIndex];
        if (!rowToDelete?.id) return;

        setDeletingRowId(rowToDelete.id);
        setIsDeleteInProgress(true);

        dispatch(deleteBundleItemAsync({ id: Number(rowToDelete.id) }))
            .unwrap()
            .then(() => {
                toast.success("Bundle Item deleted Successfully")
                fetchBundleList();
            })
            .catch(err => {
                console.error("Failed to delete bundle:", err);
                // optional: show error toast
            })
            .finally(() => {
                setDeletingRowId(null);
                setIsDeleteInProgress(false);
                setDeleteIndex(null);
                setShowDeleteModal(false);
            });
    };

    const handleSort = (column: any, sortDirection: 'asc' | 'desc') => {
        setPaginationData(prev => ({
            ...prev,
            sort_by: column.sortField,
            sort_order: sortDirection,
            page: 1,
        }));
    };

    const handlePageChange = (page: number) => {
        setPaginationData(prev => ({
            ...prev,
            page,
        }));
    };

    const handleRowsPerPageChange = (newLimit: number, page: number) => {
        setPaginationData(prev => ({
            ...prev,
            limit: newLimit,
            page: 1, // reset to first page
        }));
    };

    const columns: TableColumn<any>[] = [
        {
            name: "Bundle Name",
            selector: (row) => row.name,
            sortable: false,
            cell: (row) => (
                <div>
                    <div className="abd-name">{row.name}</div>
                </div>
            ),
            maxWidth: "220px",
            minWidth: "140px"
        },
        {
            name: "Parent Sku",
            selector: (row) => row.sku,
            sortable: false,
            cell: (row) => (
                <div>
                    <div className="abd-name">{row.sku}</div>
                </div>),
            maxWidth: "220px",
            minWidth: "140px"
        },
        {
            name: "Components",
            selector: (row) => row.component_count,
            sortable: false,
            cell: (row) => (
                <div>
                    <div style={{ color: "#374151", fontWeight: 600 }}>{row.component_count} distinct items</div>
                    <div>
                        <span
                            className="view-details-link"
                            onClick={(e) => openPopover(e, row.bundle_info)}
                        >
                            View Details
                        </span>
                    </div>
                </div>),
            maxWidth: "220px",
            minWidth: "140px"
        },
        {
            name: "Total Qty",
            selector: (row) => row.total_qty,
            sortable: false,
            cell: (row) => <div style={{ fontWeight: 600, justifyContent: "center" }}>{row.total_qty}</div>,
            maxWidth: "220px",
            minWidth: "130px"
        },
        {
            name: "Status",
            selector: (row) => row.is_active,
            sortable: false,
            cell: (row) => (
                <div
                    className={`bundle-status ${row.is_active ? "active" : "inactive"}`}
                >
                    {row.is_active ? "Active" : "Inactive"}
                </div>
            ),
            center: true,
            maxWidth: "120px",
            minWidth: "100px"
        },
        {
            name: (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>Created At</span>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
                    >
                        <span style={{ cursor: "pointer", color: "darkgray" }}>
                            <i className="fa fa-info-circle" />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            center: true,
            maxWidth: "220px",
            minWidth: "180px",
            sortable: true,
            sortField: 'created_at',
            cell: (row) => (row?.created_at ? formatDateLocal(row.created_at) : ""),
        },
        {
            name: (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>Updated At</span>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
                    >
                        <span style={{ cursor: "pointer", color: "darkgray" }}>
                            <i className="fa fa-info-circle" />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            center: true,
            cell: (row) => (row?.updated_at ? formatDateLocal(row.updated_at) : "-"),
            maxWidth: "220px",
            minWidth: "180px",
            sortable: true,
            sortField: 'updated_at',
        },
        {
            name: "Action",
            cell: (_row, index) => {
                const row = bundleList[index];
                const isThisRowDeleting = deletingRowId === row?.id;
                return (
                    <div className="abd-actions d-flex align-items-center gap-2">
                        <button
                            className="btn btn-icon btn-bg-light btn-color-primary btn-sm me-3 mt-1"
                            onClick={() => handleEdit(row)}
                            disabled={isDeleteInProgress} // disable all buttons while deleting
                        >
                            <KTIcon iconName="pencil" className="fs-4" />
                        </button>

                        {isDelete && ( <button
                            className="btn btn-icon btn-bg-light btn-color-danger btn-sm mt-1"
                            onClick={() => handleDeleteClick(index)}
                            disabled={isDeleteInProgress}
                        >
                            {isThisRowDeleting ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                                <KTIcon iconName="trash" className="fs-4" />
                            )}
                        </button> )}
                    </div>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            maxWidth: "220px",
            minWidth: "140px"
        }
    ]

    return (
        <div className="abd-container">
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
                Kits & Bundles
            </Typography>
            <Typography gutterBottom sx={{ fontWeight: 500, mb: 1.5 }}>
                Use this page to link a single marketplace listing to multiple component items in your ERP/Ecom platform.
                When a bundle is sold, the system will automatically "translate" the order into its individual child items.
                This ensures your warehouse picks the correct components and your ERP/ecom inventory remains accurate for every part of the kit.
            </Typography>
            <div className="abd-card">
                <div className="abd-header">
                    <div className="abd-channel-tabs">
                        <ul
                            ref={channelTabsRef}
                            className="abd-channel-tabs-scroll"
                        >
                            {secondaryStorePlatforms.length &&
                                secondaryStorePlatforms.map((item, index) => {
                                    const platformLabel = getConnectedPlatformLabel(item);
                                    const platformTooltip = getConnectedPlatformTooltip(item);
                                    const isActive = currentActiveTab === index + 1;

                                    return (
                                        <li key={'tab-items' + index}>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`channel-tooltip-${item?.id ?? index}`}>
                                                        {platformTooltip}
                                                    </Tooltip>
                                                }
                                            >
                                                <button
                                                    type="button"
                                                    className={clsx("abd-channel-tab", {
                                                        active: isActive,
                                                    })}
                                                    onClick={() => {
                                                        setCurrentActiveTab(index + 1);
                                                        setSearchString("");
                                                        if (item?.id != null) {
                                                            setCurrentPlatformId(item.id);
                                                        }
                                                    }}
                                                >
                                                    <img
                                                        src={item.platform_info?.logo_ref}
                                                        alt={platformLabel}
                                                    />
                                                    <span>{platformLabel}</span>
                                                </button>
                                            </OverlayTrigger>
                                        </li>
                                    );
                                })}
                        </ul>

                        {hasChannelOverflow && (
                            <div className="abd-channel-tabs-nav">
                                <button
                                    type="button"
                                    onClick={() => scrollChannels(-1)}
                                    disabled={!canScrollChannelsLeft}
                                    aria-label="Scroll channels backward"
                                >
                                    <i className="bi bi-chevron-left" />
                                </button>

                                <button
                                    type="button"
                                    onClick={() => scrollChannels(1)}
                                    disabled={!canScrollChannelsRight}
                                    aria-label="Scroll channels forward"
                                >
                                    <i className="bi bi-chevron-right" />
                                </button>
                            </div>
                        )}
                    </div>

                    {isWrite && ( <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        <Button
                            variant="contained"
                            onClick={handleCreate}
                            disabled={currentPlatformId == null}
                        >
                            + Create New Bundle
                        </Button>
                    </div> )}
                </div>

                {secondaryStorePlatforms.length === 0 && (
                    <div className="alert alert-warning mb-3" role="alert">
                        No destination marketplace is available for bundle definitions. Connect a marketplace
                        channel from your integrations, then return here.
                    </div>
                )}

                <div className="border border-2 rounded p-0 mb-5">
                    {/* SEARCH */}
                    <div style={{ backgroundColor: "rgba(33,37,41, 0.03)", borderBottom: "1px solid #e5e7eb",padding:"10px" }}>
                        <div className="position-relative" style={{ width: "15rem", padding: "5px" }}>
                            {/* SEARCH */}
                            {searchString === "" && (
                                <i className="bi bi-arrow-return-left position-absolute top-50 translate-middle-y end-0 me-3 text-secondary"></i>
                            )}
                            <input
                                className="form-control"
                                type="search"
                                placeholder="Search"
                                value={searchString}
                                onChange={(e) => setSearchString(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        setPaginationData((prev) => ({
                                            ...prev,
                                            search: searchString,
                                            page_num: 1,
                                            sort_order: "desc",
                                        }));
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <TableLoadingShell loading={loading}>
                    <DataTable
                        fixedHeader
                        persistTableHead
                        columns={columns}
                        data={bundleList}
                        noHeader

                        pagination
                        paginationServer
                        paginationTotalRows={pagination?.count || 0}
                        paginationPerPage={paginationData.limit}
                        paginationDefaultPage={pagination?.pageNumber || 1}

                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handleRowsPerPageChange}

                        highlightOnHover
                        progressPending={false}
                        sortServer
                        onSort={handleSort}
                        customStyles={{
                            table: {
                                style: { minHeight: "55vh" },
                            },
                            headRow: {
                                style: { backgroundColor: "#fff", borderBottom: "1px solid #e5e7eb" },
                            },
                            rows: {
                                style: { minHeight: "48px", borderBottom: "1px solid #f3f4f6" },
                            },
                            headCells: {
                                style: { fontWeight: 'bold', fontSize: '14px' },
                            },
                        }}
                    />
                    </TableLoadingShell>
                </div>

                {/* Popover for bundle_info */}
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={closePopover}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                    <div style={{
                        padding: "8px", maxWidth: "22rem !important", width: "25rem", minHeight: "17rem", height: "16rem",
                    }}>
                        <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 8 }}>
                            Bundle Contents:
                        </Typography>
                        <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 8 }}>
                            {popoverItems?.map((it, i) => (
                                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                                    <div><div style={{ fontSize: 14 }}>{it.name}<br /><span style={{ fontSize: "11px" }}>{it.sku}</span></div></div>
                                    <div style={{ fontWeight: 700, color: "#2563eb" }}>{it.quantity}x</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Popover>
            </div>
            <BundleEditorModal
                open={openPopup}
                onClose={() => setOpenPopup(false)}
                initialBundle={editingBundleId}
                currentPlatformId={currentPlatformId}
                onSave={(bundle: BundleData, closeAfterSave) => {
                    setOpenPopup(false);
                    fetchBundleList();
                }}
            />
            <CommonAlertModal
                title="Delete Bundle Item"
                subtitle="Are you sure you want to delete this bundle?"
                isVisible={showDeleteModal}
                onCancel={cancelDelete}
                onSuccess={confirmDelete}
                cancelBtnTitle="No"
                successBtnTitle="Yes, Delete"
                successBtnClasses="btn btn-sm btn-danger m-2"
                showSaveButton={true}
                showCloseButton={true}
                isTermsVisible={false}
                modalClassName="bundle-editor-modal"
            />

        </div>
    );
}
